/**
 * Statlign SEO Intent & Keyword Registry
 *
 * Purpose:
 * - Provide tightly relevant keyword/entity clusters to use alongside
 *   page-specific SEO metadata.
 * - Avoid keyword stuffing and unrelated search intent.
 * - Support multilingual SEO.
 * - Keep keywords aligned with the actual content available on the page.
 *
 * IMPORTANT:
 * This registry is NOT intended to be injected wholesale into every page.
 * A page should receive only the clusters relevant to its actual subject.
 */

export type SEOLocale =
  | "en"
  | "fr"
  | "de"
  | "es"
  | "pt"
  | "it"
  | "ru"
  | "tr"
  | "pl"
  | "nl"
  | "sv"
  | "el"
  | "id"
  | "hi"
  | "ja"
  | "ko"
  | "fil"
  | "am"
  | "ar"
  | "zh"
  | "zh-TW";

export type SEOIntent = "informational" | "navigational" | "commercial" | "transactional";

export interface KeywordCluster {
  primary: string[];
  secondary?: string[];
  longTail?: string[];
  entities?: string[];
  intents?: SEOIntent[];
}

export interface SEOContext {
  locale: SEOLocale;
  sport?: string;
  competition?: string;
  market?: string;
  pageType?: string;
}

/* -------------------------------------------------------------------------- */
/* CORE BRAND                                                                  */
/* -------------------------------------------------------------------------- */

const EN_BRAND: KeywordCluster = {
  primary: ["Statlign", "Statlign sports intelligence"],

  secondary: [
    "sports intelligence platform",
    "sports statistics",
    "sports analysis",
    "sports predictions",
    "sports data",
  ],

  longTail: [
    "sports intelligence platform",
    "AI sports analysis",
    "sports statistics and analysis",
    "explainable sports predictions",
    "sports prediction analysis",
  ],

  entities: ["Statlign", "Sports Intelligence"],

  intents: ["navigational", "informational"],
};

/* -------------------------------------------------------------------------- */
/* FOOTBALL                                                                   */
/* -------------------------------------------------------------------------- */

const EN_FOOTBALL: KeywordCluster = {
  primary: ["football predictions", "football statistics", "football analysis"],

  secondary: [
    "football match predictions",
    "football statistics today",
    "football match analysis",
    "football form guide",
    "football results",
    "football fixtures",
  ],

  longTail: [
    "football predictions today",
    "football match analysis today",
    "football team form",
    "football head to head statistics",
    "football match statistics",
  ],

  entities: ["Football", "Soccer"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* BTTS                                                                       */
/* -------------------------------------------------------------------------- */

const EN_BTTS: KeywordCluster = {
  primary: ["BTTS predictions", "both teams to score predictions"],

  secondary: ["BTTS today", "BTTS tips", "both teams to score", "BTTS statistics", "BTTS analysis"],

  longTail: [
    "BTTS predictions today",
    "both teams to score predictions today",
    "BTTS match analysis",
    "both teams to score statistics",
  ],

  entities: ["BTTS", "Both Teams To Score"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* PREMIER LEAGUE                                                              */
/* -------------------------------------------------------------------------- */

const EN_PREMIER_LEAGUE: KeywordCluster = {
  primary: ["Premier League predictions", "Premier League fixtures", "Premier League results"],

  secondary: [
    "Premier League statistics",
    "Premier League match analysis",
    "Premier League standings",
    "Premier League team form",
  ],

  longTail: [
    "Premier League predictions today",
    "Premier League match predictions",
    "Premier League football analysis",
    "Premier League statistics and predictions",
  ],

  entities: ["Premier League", "English Premier League", "EPL"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* UEFA CHAMPIONS LEAGUE                                                      */
/* -------------------------------------------------------------------------- */

const EN_CHAMPIONS_LEAGUE: KeywordCluster = {
  primary: ["Champions League predictions", "Champions League fixtures", "Champions League results"],

  secondary: [
    "UEFA Champions League",
    "Champions League statistics",
    "Champions League match analysis",
    "Champions League standings",
  ],

  longTail: [
    "Champions League predictions today",
    "Champions League match predictions",
    "Champions League football analysis",
    "Champions League statistics",
  ],

  entities: ["UEFA Champions League", "Champions League", "UEFA"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* US SPORTS                                                                   */
/* -------------------------------------------------------------------------- */

const EN_NFL: KeywordCluster = {
  primary: ["NFL predictions", "NFL picks", "NFL analysis"],

  secondary: ["NFL statistics", "NFL fixtures", "NFL scores", "NFL standings", "NFL team form"],

  longTail: [
    "NFL predictions today",
    "NFL game predictions",
    "NFL match analysis",
    "NFL team statistics",
    "NFL game statistics",
  ],

  entities: ["NFL", "National Football League"],

  intents: ["informational", "commercial"],
};

const EN_NBA: KeywordCluster = {
  primary: ["NBA predictions", "NBA picks", "NBA analysis"],

  secondary: ["NBA statistics", "NBA scores", "NBA fixtures", "NBA standings", "NBA team form"],

  longTail: ["NBA predictions today", "NBA game predictions", "NBA match analysis", "NBA team statistics"],

  entities: ["NBA", "National Basketball Association"],

  intents: ["informational", "commercial"],
};

const EN_MLB: KeywordCluster = {
  primary: ["MLB predictions", "MLB picks", "MLB analysis"],

  secondary: ["MLB statistics", "MLB scores", "MLB schedule", "MLB standings", "MLB team form"],

  longTail: ["MLB predictions today", "MLB game predictions", "MLB match analysis", "MLB team statistics"],

  entities: ["MLB", "Major League Baseball"],

  intents: ["informational", "commercial"],
};

const EN_NHL: KeywordCluster = {
  primary: ["NHL predictions", "NHL picks", "NHL analysis"],

  secondary: ["NHL statistics", "NHL scores", "NHL schedule", "NHL standings", "NHL team form"],

  longTail: ["NHL predictions today", "NHL game predictions", "NHL match analysis", "NHL team statistics"],

  entities: ["NHL", "National Hockey League"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* OTHER SPORTS                                                               */
/* -------------------------------------------------------------------------- */

const EN_BASKETBALL: KeywordCluster = {
  primary: ["basketball predictions", "basketball statistics", "basketball analysis"],

  secondary: ["basketball game predictions", "basketball team statistics", "basketball match analysis"],

  longTail: ["basketball predictions today", "basketball game analysis", "basketball team form"],

  entities: ["Basketball"],

  intents: ["informational", "commercial"],
};

const EN_TENNIS: KeywordCluster = {
  primary: ["tennis predictions", "tennis statistics", "tennis analysis"],

  secondary: ["tennis match predictions", "tennis player statistics", "tennis match analysis"],

  longTail: ["tennis predictions today", "tennis match analysis", "tennis player form"],

  entities: ["Tennis"],

  intents: ["informational", "commercial"],
};

const EN_CRICKET: KeywordCluster = {
  primary: ["cricket predictions", "cricket statistics", "cricket analysis"],

  secondary: ["cricket match predictions", "cricket team statistics", "cricket match analysis"],

  longTail: ["cricket predictions today", "cricket match analysis", "cricket team form"],

  entities: ["Cricket"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* FRENCH                                                                      */
/* -------------------------------------------------------------------------- */

const FR_FOOTBALL: KeywordCluster = {
  primary: ["pronostic football", "pronostics foot", "analyse football"],

  secondary: [
    "pronostic foot du jour",
    "pronostic football aujourd'hui",
    "statistiques football",
    "analyse match football",
    "résultats football",
    "calendrier football",
  ],

  longTail: [
    "pronostic foot du jour",
    "analyse match de football",
    "statistiques match football",
    "forme des équipes football",
  ],

  entities: ["Football", "Pronostic football"],

  intents: ["informational", "commercial"],
};

const FR_BETTING: KeywordCluster = {
  primary: ["pronostic foot", "pronostics sportifs"],

  secondary: ["pronostic football gratuit", "pronostic foot aujourd'hui", "analyse paris sportifs"],

  longTail: ["pronostic football du jour", "analyse statistiques football"],

  entities: ["Pronostics sportifs"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* GERMAN                                                                      */
/* -------------------------------------------------------------------------- */

const DE_FOOTBALL: KeywordCluster = {
  primary: ["Fußball Vorhersagen", "Fußball Prognosen", "Fußball Analyse"],

  secondary: [
    "Fußball Tipps heute",
    "Fußball Statistiken",
    "Fußball Ergebnisse",
    "Fußball Spielanalyse",
    "Bundesliga Prognose",
  ],

  longTail: [
    "Fußball Prognose heute",
    "Fußball Spielanalyse heute",
    "Bundesliga Prognosen",
    "Fußball Statistiken heute",
  ],

  entities: ["Fußball", "Bundesliga"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* CHINESE                                                                     */
/* -------------------------------------------------------------------------- */

const ZH_FOOTBALL: KeywordCluster = {
  primary: ["足球预测", "足球分析", "足球数据"],

  secondary: ["足球比赛预测", "足球比分", "足球赛程", "足球比赛分析", "足球统计"],

  longTail: ["今日足球预测", "足球比赛数据分析", "足球球队数据", "足球比赛统计"],

  entities: ["足球", "足球预测", "体育数据"],

  intents: ["informational", "commercial"],
};

/* -------------------------------------------------------------------------- */
/* PAGE-TYPE DEFAULTS                                                          */
/* -------------------------------------------------------------------------- */

const PAGE_TYPE_CLUSTERS: Record<string, KeywordCluster> = {
  home: EN_BRAND,

  predictions: {
    primary: ["sports predictions", "sports match predictions"],
    secondary: ["today's sports predictions", "match predictions", "sports analysis"],
    longTail: ["sports predictions today", "match predictions and analysis"],
    entities: ["Sports Predictions", "Sports Intelligence"],
    intents: ["informational", "commercial"],
  },

  live: {
    primary: ["live sports", "live scores", "live sports statistics"],
    secondary: ["live match statistics", "live match analysis", "live scores today"],
    longTail: ["live sports scores and statistics", "live match analysis"],
    entities: ["Live Sports", "Live Scores"],
    intents: ["informational", "navigational"],
  },

  guides: {
    primary: ["sports betting guide", "sports analysis guide"],
    secondary: ["sports statistics explained", "sports betting explained", "sports analytics guide"],
    longTail: [
      "how sports predictions work",
      "how to understand sports statistics",
      "sports betting terminology explained",
    ],
    entities: ["Sports Analytics", "Sports Statistics"],
    intents: ["informational"],
  },

  highOdds: {
    primary: ["high odds predictions", "high odds football predictions"],
    secondary: ["high odds match predictions", "high odds football analysis", "high odds statistics"],
    longTail: ["high odds predictions today", "high odds football predictions today", "high odds match analysis"],
    entities: ["High Odds", "Sports Predictions"],
    intents: ["informational", "commercial"],
  },

  btts: EN_BTTS,
};

/* -------------------------------------------------------------------------- */
/* SPORT ROUTES                                                                */
/* -------------------------------------------------------------------------- */

const SPORT_CLUSTERS: Record<string, KeywordCluster> = {
  football: EN_FOOTBALL,
  soccer: EN_FOOTBALL,
  basketball: EN_BASKETBALL,
  tennis: EN_TENNIS,
  cricket: EN_CRICKET,
  nfl: EN_NFL,
  nba: EN_NBA,
  mlb: EN_MLB,
  nhl: EN_NHL,
};

/* -------------------------------------------------------------------------- */
/* COMPETITION CLUSTERS                                                        */
/* -------------------------------------------------------------------------- */

const COMPETITION_CLUSTERS: Record<string, KeywordCluster> = {
  "premier-league": EN_PREMIER_LEAGUE,
  "champions-league": EN_CHAMPIONS_LEAGUE,
};

/* -------------------------------------------------------------------------- */
/* UTILITIES                                                                   */
/* -------------------------------------------------------------------------- */

function normalize(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

function unique(values: string[] = []): string[] {
  return [...new Set(values.map(normalize).filter(Boolean))];
}

function mergeClusters(...clusters: (KeywordCluster | undefined)[]): KeywordCluster {
  const valid = clusters.filter(Boolean) as KeywordCluster[];

  return {
    primary: unique(valid.flatMap((c) => c.primary ?? [])),
    secondary: unique(valid.flatMap((c) => c.secondary ?? [])),
    longTail: unique(valid.flatMap((c) => c.longTail ?? [])),
    entities: unique(valid.flatMap((c) => c.entities ?? [])),
    intents: [...new Set(valid.flatMap((c) => c.intents ?? []))],
  };
}

/**
 * Prefix matching that respects route segments.
 *
 * /predictions matches:
 * /predictions
 * /predictions/today
 *
 * But does NOT match:
 * /predictionsomething
 */
function routeMatches(route: string, prefix: string): boolean {
  const normalizedRoute = route.replace(/\/+$/, "") || "/";
  const normalizedPrefix = prefix.replace(/\/+$/, "") || "/";

  if (normalizedPrefix === "/") {
    return normalizedRoute === "/";
  }

  return normalizedRoute === normalizedPrefix || normalizedRoute.startsWith(`${normalizedPrefix}/`);
}

/* -------------------------------------------------------------------------- */
/* PUBLIC API                                                                  */
/* -------------------------------------------------------------------------- */

export function getSEOKeywordCluster(context: SEOContext): KeywordCluster {
  const { locale, sport, competition, market, pageType } = context;

  /*
   * Start with the page type.
   */
  let cluster = pageType ? PAGE_TYPE_CLUSTERS[pageType] : undefined;

  /*
   * Add sport-specific relevance.
   */
  if (sport) {
    cluster = mergeClusters(cluster, SPORT_CLUSTERS[normalize(sport)]);
  }

  /*
   * Add competition-specific relevance.
   */
  if (competition) {
    cluster = mergeClusters(cluster, COMPETITION_CLUSTERS[normalize(competition)]);
  }

  /*
   * Add market-specific relevance.
   */
  if (market && normalize(market) === "btts") {
    cluster = mergeClusters(cluster, EN_BTTS);
  }

  /*
   * Locale-specific enrichment.
   *
   * Do NOT simply dump an entire language keyword pool into every page.
   */
  if (locale === "fr") {
    cluster = mergeClusters(cluster, FR_FOOTBALL);
  }

  if (locale === "de") {
    cluster = mergeClusters(cluster, DE_FOOTBALL);
  }

  if (locale === "zh" || locale === "zh-TW") {
    cluster = mergeClusters(cluster, ZH_FOOTBALL);
  }

  /*
   * Always retain brand/entity relevance.
   */
  if (locale === "en") {
    cluster = mergeClusters(EN_BRAND, cluster);
  }

  return (
    cluster ?? {
      primary: [],
      secondary: [],
      longTail: [],
      entities: [],
      intents: [],
    }
  );
}

/* -------------------------------------------------------------------------- */
/* EXTENDED PAGE CLUSTERS (routes that previously had no keyword pool)          */
/* -------------------------------------------------------------------------- */

const EXTRA_CLUSTERS: Record<string, KeywordCluster> = {
  footballPredictions: {
    primary: ["football predictions today", "soccer predictions"],
    secondary: ["football betting tips", "football tips today", "soccer betting tips"],
    longTail: ["ai football predictions today", "football predictions with odds"],
    entities: ["Football"],
  },
  ufc: {
    primary: ["ufc predictions", "mma predictions"],
    secondary: ["ufc picks", "ufc betting tips", "mma picks tonight"],
    longTail: ["ufc fight predictions this weekend", "ufc picks and odds"],
    entities: ["UFC", "Mixed Martial Arts"],
  },
  boxing: {
    primary: ["boxing predictions", "boxing picks"],
    secondary: ["boxing betting tips", "boxing odds"],
    longTail: ["boxing picks tonight", "boxing fight predictions"],
    entities: ["Boxing"],
  },
  horseRacing: {
    primary: ["horse racing tips today", "horse racing predictions"],
    secondary: ["racing tips", "horse racing picks"],
    longTail: ["horse racing tips and odds today"],
    entities: ["Horse Racing"],
  },
  jackpot: {
    primary: ["jackpot predictions", "mega jackpot predictions"],
    secondary: ["jackpot tips", "sportpesa jackpot predictions", "midweek jackpot predictions"],
    longTail: ["mega jackpot predictions this weekend"],
    entities: ["Jackpot"],
  },
  safeBets: {
    primary: ["safe bets today", "safe football predictions"],
    secondary: ["high confidence betting tips", "double chance tips"],
    longTail: ["safe bet predictions for today"],
    entities: ["Double Chance", "Draw No Bet"],
  },
  trackRecord: {
    primary: ["prediction track record", "betting tips results"],
    secondary: ["prediction accuracy", "verified betting results"],
    longTail: ["verified football prediction results"],
    entities: ["Track Record"],
  },
  overUnder: {
    primary: ["over 2.5 goals predictions", "over under predictions"],
    secondary: ["over 1.5 goals tips", "goals betting tips"],
    longTail: ["over 2.5 goals predictions today"],
    entities: ["Over/Under"],
  },
  doubleChance: {
    primary: ["double chance predictions", "double chance tips"],
    secondary: ["1x2 double chance", "safe double chance bets"],
    longTail: ["double chance predictions today"],
    entities: ["Double Chance"],
  },
  drawNoBet: {
    primary: ["draw no bet predictions", "draw no bet tips"],
    secondary: ["dnb betting tips"],
    longTail: ["draw no bet predictions today"],
    entities: ["Draw No Bet"],
  },
  corners: {
    primary: ["corner predictions", "corners betting tips"],
    secondary: ["total corners tips"],
    longTail: ["corner predictions today"],
    entities: ["Corners"],
  },
  cards: {
    primary: ["cards predictions", "booking points tips"],
    secondary: ["total cards betting tips"],
    longTail: ["cards predictions today"],
    entities: ["Cards"],
  },
  leagues: {
    primary: ["league predictions", "football predictions by league"],
    secondary: ["premier league predictions", "la liga predictions", "serie a predictions"],
    entities: ["Football Leagues"],
  },
  teams: {
    primary: ["team predictions", "team form and odds"],
    secondary: ["team betting tips", "team stats"],
    entities: ["Teams"],
  },
  players: {
    primary: ["football player stats", "player form"],
    secondary: ["anytime goalscorer tips", "player impact ratings"],
    entities: ["Players"],
  },
  fixtures: {
    primary: ["football fixtures and predictions", "match predictions"],
    secondary: ["fixtures and odds", "match previews"],
    entities: ["Fixtures"],
  },
  blog: {
    primary: ["sports betting blog", "betting insights"],
    secondary: ["match previews", "betting strategy"],
    entities: ["Sports Analysis"],
  },
  faq: {
    primary: ["football predictions faq", "how ai predictions work"],
    secondary: ["betting tips explained"],
    entities: ["Statlign"],
  },
  aiPredictions: {
    primary: ["ai football predictions", "ai betting tips"],
    secondary: ["machine learning predictions", "ai soccer predictions"],
    longTail: ["ai football predictions today"],
    entities: ["Artificial Intelligence", "Football"],
  },
};

/** Compact league cluster: "<name> predictions" owns the primary keyword. */
function league(name: string, aliases: string[] = []): KeywordCluster {
  const n = name.toLowerCase();
  return {
    primary: [`${n} predictions`, `${n} betting tips`],
    secondary: [`${n} predictions today`, `${n} odds`],
    longTail: [`${n} match predictions this week`],
    entities: [name, ...aliases],
  };
}

const MORE_COMPETITIONS: Record<string, KeywordCluster> = {
  epl: EN_PREMIER_LEAGUE,
  "la-liga": league("La Liga", ["LaLiga"]),
  "serie-a": league("Serie A"),
  bundesliga: league("Bundesliga"),
  "ligue-1": league("Ligue 1"),
  championship: league("Championship", ["EFL Championship"]),
  "europa-league": league("Europa League", ["UEFA Europa League"]),
  "conference-league": league("Conference League", ["UEFA Conference League"]),
  mls: league("MLS", ["Major League Soccer"]),
  eredivisie: league("Eredivisie"),
  "primeira-liga": league("Primeira Liga"),
  brasileirao: league("Brasileirao", ["Serie A Brazil"]),
  "saudi-pro-league": league("Saudi Pro League"),
  "fa-cup": league("FA Cup"),
  "world-cup": league("World Cup", ["FIFA World Cup"]),
};

const MORE_MARKETS: Record<string, KeywordCluster> = {
  "both-teams-to-score": EN_BTTS,
  "over-2-5": EXTRA_CLUSTERS["overUnder"]!,
  "under-2-5": {
    primary: ["under 2.5 goals predictions", "under 2.5 tips"],
    secondary: ["low scoring match predictions"],
    longTail: ["under 2.5 goals predictions today"],
    entities: ["Under 2.5 Goals"],
  },
  "1x2": {
    primary: ["1x2 predictions", "match result predictions"],
    secondary: ["win draw win tips", "full time result tips"],
    longTail: ["1x2 predictions today"],
    entities: ["1X2"],
  },
  "correct-score": {
    primary: ["correct score predictions", "exact score tips"],
    secondary: ["correct score tips today"],
    longTail: ["correct score predictions today"],
    entities: ["Correct Score"],
  },
  htft: {
    primary: ["ht/ft predictions", "half time full time tips"],
    secondary: ["htft betting tips"],
    longTail: ["ht ft predictions today"],
    entities: ["HT/FT"],
  },
  accumulator: {
    primary: ["accumulator tips", "football acca predictions"],
    secondary: ["acca tips today", "multi bet tips"],
    longTail: ["football accumulator tips today"],
    entities: ["Accumulator"],
  },
  draw: {
    primary: ["draw predictions", "draw tips"],
    secondary: ["draw betting tips"],
    longTail: ["draw predictions today"],
    entities: ["Draw"],
  },
};

/** Non-betting clusters for trust, legal and account pages. */
const UTILITY_CLUSTERS: Record<string, KeywordCluster> = {
  terms: { primary: ["statlign terms of service"], secondary: ["terms and conditions"], entities: ["Statlign"] },
  privacy: { primary: ["statlign privacy policy"], secondary: ["cookie policy", "data protection"], entities: ["Statlign"] },
  responsibleGambling: {
    primary: ["responsible gambling", "gambling help"],
    secondary: ["problem gambling support", "set betting limits"],
    entities: ["Responsible Gambling"],
  },
  contact: { primary: ["contact statlign"], secondary: ["statlign support", "press and partnerships"], entities: ["Statlign"] },
};

const ALL_CLUSTERS: Record<string, KeywordCluster> = {
  ...PAGE_TYPE_CLUSTERS,
  ...SPORT_CLUSTERS,
  ...COMPETITION_CLUSTERS,
  ...MORE_COMPETITIONS,
  ...EXTRA_CLUSTERS,
  ...MORE_MARKETS,
  ...UTILITY_CLUSTERS,
  mma: EXTRA_CLUSTERS["ufc"]!,
  "over-under": EXTRA_CLUSTERS["overUnder"]!,
  "double-chance": EXTRA_CLUSTERS["doubleChance"]!,
  "draw-no-bet": EXTRA_CLUSTERS["drawNoBet"]!,
  nigeria: {
    primary: ["football predictions nigeria", "betting tips nigeria"],
    secondary: ["naija football tips", "bet9ja predictions", "sportybet predictions", "npfl predictions"],
    longTail: ["football predictions today nigeria", "pay for football tips in naira"],
    entities: ["Nigeria", "NPFL", "Naira"],
    intents: ["commercial", "informational"],
  },
};

export interface SeoTopic {
  /** Cluster keys, most specific first, e.g. ["nba", "predictions"]. */
  topics: string[];
  /** Page-specific names (team, league, fighter) placed first. */
  entities?: string[];
}

export interface SeoKeywords {
  primary: string;
  supporting: string[];
  entities: string[];
  /** Comma list for the keywords meta tag (max 8, deduped). */
  meta: string;
}

/** Deterministic keyword pick for a page — the first topic owns the primary keyword. */
export function seoFor({ topics, entities = [] }: SeoTopic): SeoKeywords {
  const clusters = topics.map((t) => ALL_CLUSTERS[t]).filter(Boolean) as KeywordCluster[];
  const merged = mergeClusters(...clusters);
  const primary = merged.primary?.[0] ?? "sports predictions";
  const supporting = unique([
    ...(merged.primary ?? []).slice(1, 2),
    ...(merged.secondary ?? []).slice(0, 2),
    ...(merged.longTail ?? []).slice(0, 1),
  ]).filter((k) => k !== primary).slice(0, 4);
  const ents = unique([...entities, ...(merged.entities ?? [])]);
  const meta = unique([...entities.map((e) => e.toLowerCase()), primary, ...supporting, "statlign"]).slice(0, 8).join(", ");
  return { primary, supporting, entities: ents, meta };
}
